import { useQueries } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { CheckCircle2, Circle, Calendar, Loader2 } from 'lucide-react'
import type { StudentHomework } from '../../lib/api-types'
import type { UpcomingWorkData, UpcomingWorkItem } from '../../lib/api-types'

interface ThisWeekViewProps {
  students: StudentHomework[]
}

/** Group items by date string (e.g. "2026-02-10") */
function groupByDate(items: UpcomingWorkItem[]): Map<string, UpcomingWorkItem[]> {
  const groups = new Map<string, UpcomingWorkItem[]>()
  for (const item of items) {
    const dateKey = item.start_at ? item.start_at.split('T')[0] : 'no-date'
    const existing = groups.get(dateKey)
    if (existing) {
      existing.push(item)
    } else {
      groups.set(dateKey, [item])
    }
  }
  return groups
}

function formatDayHeader(dateStr: string): string {
  if (dateStr === 'no-date') return 'Date TBD'
  const date = new Date(dateStr + 'T12:00:00')
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  })
}

export function ThisWeekView({ students }: ThisWeekViewProps) {
  const queries = useQueries({
    queries: students.map((student) => ({
      queryKey: ['parent-student-upcoming', String(student.id)],
      queryFn: async () => {
        const response = await api.get(`/api/parent/students/${student.id}/upcoming-work?days=7`)
        return response.data as UpcomingWorkData
      },
      staleTime: 5 * 60 * 1000,
    })),
  })

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {students.map((student, idx) => {
        const query = queries[idx]
        return (
          <StudentWeekCard
            key={student.id}
            student={student}
            data={query.data}
            isLoading={query.isLoading}
          />
        )
      })}
    </div>
  )
}

function StudentWeekCard({
  student,
  data,
  isLoading,
}: {
  student: StudentHomework
  data: UpcomingWorkData | undefined
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="card border-gray-700/50 flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-quest-primary" />
      </div>
    )
  }

  // Combine tomorrow + later (skip today — the Today view handles that)
  const futureItems = [...(data?.tomorrow || []), ...(data?.later || [])]
  const totalUpcoming = futureItems.length
  const testCount = futureItems.filter((i) => i.is_test).length
  const dayGroups = groupByDate(futureItems)

  return (
    <div className="card border-gray-700/50">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">{student.name}</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">Level</span>
          <div className="level-badge text-sm">{student.level}</div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-2 mb-4">
        <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-700 text-gray-300">
          {totalUpcoming} upcoming
        </span>
        {testCount > 0 && (
          <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-orange-500/20 text-orange-400">
            {testCount} test{testCount > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Day groups */}
      {totalUpcoming > 0 ? (
        <div className="space-y-4">
          {Array.from(dayGroups.entries()).map(([dateKey, items]) => (
            <div key={dateKey}>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {formatDayHeader(dateKey)}
              </h3>
              <div className="space-y-1.5">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg ${
                      item.is_test
                        ? 'bg-orange-900/20 border border-orange-500/30'
                        : item.has_scan
                          ? 'bg-green-900/20 border border-green-500/30'
                          : 'bg-surface-card/50'
                    }`}
                  >
                    {item.has_scan ? (
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 text-gray-600 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <p className="text-xs text-gray-500">{item.course}</p>
                    </div>
                    {item.is_test && (
                      <span className="px-2 py-0.5 text-xs rounded bg-orange-500/20 text-orange-400 flex-shrink-0">
                        TEST
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>No upcoming work this week</p>
        </div>
      )}
    </div>
  )
}
